import { AbstractClient, CompositionClient } from "@/app/client";
import type { UUID } from "crypto";

export interface PrintJobCreateRequest {
  copies?: number;
  withoutPrice?: boolean;
  printerPrefix?: string; // Optional override for printer routing prefix
}

export interface PrintJobResponse {
  id: UUID;
  status: string; // "queued", "test_mode"
  copies: number;
  created_at: string;
}

export default class APIPrintJobsSub extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  /**
   * Create a print job by uploading a PDF file
   *
   * The backend will automatically use the printingConfig feature flag to
   * determine the printer prefix. You can optionally override this with
   * the printerPrefix option.
   *
   * @throws Error with message containing "503" for service unavailable
   * @throws Error with message containing "500" for server errors
   */
  async createPrintJob(
    pdfBlob: Blob,
    filename: string,
    options?: PrintJobCreateRequest,
  ): Promise<PrintJobResponse> {
    const formData = new FormData();
    formData.append("pdf_file", pdfBlob, filename);

    if (options?.copies !== undefined) {
      formData.append("copies", options.copies.toString());
    }

    if (options?.withoutPrice !== undefined) {
      formData.append("without_price", options.withoutPrice.toString());
    }

    if (options?.printerPrefix !== undefined) {
      formData.append("printer_prefix", options.printerPrefix);
    }

    const response = await this._fetch({
      method: "POST",
      url: "/print-jobs/",
      body: formData,
      bodyMime: "multipart/form-data",
      auth: ["OAuth2PasswordBearer"],
    });

    // Handle error responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const detail = errorData?.detail || "Unknown error";

      if (response.status === 503) {
        throw new Error(`Service unavailable: ${detail}`);
      } else if (response.status === 500) {
        throw new Error(`Server error: ${detail}`);
      } else if (response.status === 400) {
        throw new Error(`Invalid request: ${detail}`);
      }
      throw new Error(`Print job failed: ${detail}`);
    }

    if (response.headers.get("Content-Type")?.includes("application/json")) {
      return response.json();
    }
    throw new Error("Bad content type");
  }
}
